/* eslint-disable */
import { defineStore } from 'pinia';
import { usePageStore } from '@/store/pinia/page';
import { useUsersStore } from '@/store/pinia/users';
import { useMeetingStore } from '@/store/pinia/meeting';
import { nM } from '@/legacy';
import { frameRateMapReverse, minUsersForHighQualityVideo, resolutionMapReverse } from '../../shared/constants';

const flags = {
  dialogue: 'CREATIVE_DIALOGUE',
  feedback: 'FEEDBACK_PHASE',
};

export const useFeaturesStore = defineStore('features', {
  state() {
    return {
      minimumSupportedAppVersion: {
        macOS: null,
        windowsOS: null,
      },
      isOnOutDatedApp: false,
      shouldShowWelcomeVideo: false,
      debug: false,
      flags: __FEATURE_FLAGS__, // eslint-disable-line no-undef
      customGroups: true,
      bigGroups: true,
      dialogueMaps: false,
      sandbox: false,
      showTalking: false,
      showThemes: false,
      showSentry: false,
      showAllEmotes: false,
      showExtraSettings: false,
      showConnectivityIndicator: false,
      meetingSettings: {
        audioOnly: false,
        reducedAnimation: false,
        simplifiedGraphics: false,
        theme: 'default',
        meetingSpace: 'default',
      },
      detectedCpuLevel: false,
      hasShownDegradeMessage: false,
      settings: {},
      perf: {
        uglyMode: false,
        fakeEmotes: false,
        popMovement: false,
        hideVideoWhenMoving: false,
        pauseVideoWhenMoving: false,
        flipMovement: true,
        noMask: false,
        emoteAvatarCore: false,
      },
    };
  },
  getters: {
    cpuLevel: (state) => {
      if (!state.settings.cpuLevel || state.settings.cpuLevel === 'auto') {
        return state.detectedCpuLevel;
      } else {
        return state.settings.cpuLevel;
      }
    },
    noAnimations() {
      return this.cpuLevel === 'bad';
    },
    noTransparency() {
      return this.cpuLevel === 'bad' || this.cpuLevel === 'low';
    },
    noAvatarMovement() {
      return this.cpuLevel === 'bad' || this.cpuLevel === 'low';
    },
    useBadgeEmotes() {
      return this.cpuLevel === 'bad' || this.cpuLevel === 'low';
    },
    useSpaceAnimation(state) {
      if (this.cpuLevel === 'bad' || this.cpuLevel === 'low' || this.noAnimations) {
        return false;
      }
      return state.settings.spaceAnimation;
    },
    preferredVideoQuality: (s) => {
      const usersStore = useUsersStore();
      const usersHere = usersStore.usersHere;
      const numOfIncomingVideos = usersHere.length > 0 ? usersHere.filter((u) => !u.video_muted && !u.video_muted_by_system).length : 0;
      if (numOfIncomingVideos <= minUsersForHighQualityVideo) {
        const [width, height] = resolutionMapReverse.high.split('x');
        const framerate = frameRateMapReverse.high;
        return { width: parseInt(width), height: parseInt(height), framerate: parseInt(framerate) };
      }
      const [width, height] = s.settings.resolution.split('x');
      return { width: parseInt(width), height: parseInt(height), framerate: parseInt(s.settings.frame_rate) };
    },
    theme(s) {
      let pageStore = usePageStore();
      let meetingStore = useMeetingStore();

      if (!pageStore.isMeeting) {
        return s.settings.theme;
      }

      if (meetingStore.space === 'neon') {
        return 'dark';
      }
      if (meetingStore.space === 'cafe') {
        return 'light';
      }

      return s.settings.theme === 'default' ? 'light' : s.settings.theme || 'light';
    },
  },
  actions: {
    init({ settings, config: { minimumSupportedAppVersion, shouldShowWelcomeVideo } }) {
      this.updateUserSettings(settings);
      this.minimumSupportedAppVersion = minimumSupportedAppVersion;
      this.shouldShowWelcomeVideo = shouldShowWelcomeVideo;
    },
    initMeeting() { },
    setUsersUsingOutdatedAppVersion(flag) {
      this.isOnOutDatedApp = flag;
    },
    overrideFeatureFlag(v) {
      let flag = flags[v];
      if (!flag) {
        return false;
      }
      this.flags[flag] = !this.flags[flag];
    },
    updateUserSettings(v) {
      if (v) {
        this.settings = { ...nM.toSettings(v) };
      }
    },
    setDetectedCpuLevel(v) {
      this.detectedCpuLevel = v;
    },
    setHasShownDegradeMessage(v) {
      this.hasShownDegradeMessage = v;
    },
    togglePerfOption(v) {
      this.perf[v] = !this.perf[v];
    },
    updateMeetingSettings() {
      //TODO: Wait until we have the latest perf changes before we mess with this
    },
    toggleSandbox() {
      this.sandbox = !this.sandbox;
    },
    toggleCustomGroups() {
      this.customGroups = !this.customGroups;
    },
    toggleDialogueMaps() {
      this.dialogueMaps = !this.dialogueMaps;
    },
    toggleShowTalking() {
      this.showTalking = !this.showTalking;
    },
    toggleThemes() {
      this.showThemes = !this.showThemes;
    },
    toggleSentry() {
      this.showSentry = !this.showSentry;
    },
    toggleExtraSettings() {
      this.showExtraSettings = !this.showExtraSettings;
    },
    toggleConnectivityIndicator() {
      this.showConnectivityIndicator = !this.showConnectivityIndicator;
    },
    toggleDebug() {
      this.debug = !this.debug;
    },
    setShowAllEmotes(v) {
      this.showAllEmotes = v;
    },
    setHasShownDegradeMessage(v) {
      this.hasShownDegradeMessage = v;
    },
    setWelcomeVideoWatched() {
      this.shouldShowWelcomeVideo = false;
    }
  },
});
